import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import {
  InvoicePrintSettings,
  INVOICE_PRINT_TEMPLATES,
  INVOICE_PRINT_COLOR_PRESETS,
} from '../../lib/invoicePrintSettings';

interface InvoicePrintSettingsFormProps {
  settings: InvoicePrintSettings;
  onChange: (settings: InvoicePrintSettings) => void;
  disabled?: boolean;
}

const InvoicePrintSettingsForm: React.FC<InvoicePrintSettingsFormProps> = ({
  settings,
  onChange,
  disabled = false,
}) => {
  const set = (patch: Partial<InvoicePrintSettings>) => {
    if (disabled) return;
    onChange({ ...settings, ...patch });
  };

  const toggle = (key: keyof InvoicePrintSettings) => () => {
    if (disabled) return;
    onChange({ ...settings, [key]: !settings[key as keyof InvoicePrintSettings] });
  };

  const toggleRow = (label: string, key: keyof InvoicePrintSettings) => (
    <label key={key} className={`flex items-center justify-between py-2 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <span className="text-sm text-slate-700">{label}</span>
      <div
        onClick={disabled ? undefined : toggle(key)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings[key] ? 'bg-indigo-600' : 'bg-slate-200'}`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${settings[key] ? 'translate-x-4.5' : 'translate-x-0.5'}`}
        />
      </div>
    </label>
  );

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Formato / tamaño</p>
        <div className="space-y-2">
          {INVOICE_PRINT_TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={disabled}
              onClick={() => set({ templateId: t.id })}
              className={`w-full text-left px-3 py-2.5 rounded-xl border text-sm transition-all disabled:cursor-not-allowed ${
                settings.templateId === t.id
                  ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800">{t.label}</span>
                {settings.templateId === t.id && <CheckIcon className="h-4 w-4 text-indigo-600" />}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {settings.templateId !== 'thermal' && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Color principal</p>
          <div className="flex gap-2 flex-wrap">
            {INVOICE_PRINT_COLOR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                disabled={disabled}
                onClick={() => set({ primaryColor: c })}
                style={{ background: c }}
                className={`h-7 w-7 rounded-full border-2 transition-transform disabled:cursor-not-allowed ${
                  settings.primaryColor === c
                    ? 'border-white ring-2 ring-slate-400 scale-110'
                    : 'border-white'
                }`}
                title={c}
              />
            ))}
            <input
              type="color"
              disabled={disabled}
              value={settings.primaryColor}
              onChange={(e) => set({ primaryColor: e.target.value })}
              className="h-7 w-7 rounded-full border-2 border-white cursor-pointer disabled:cursor-not-allowed"
              title="Color personalizado"
            />
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Etiqueta de copia</p>
        <div className="flex gap-2 flex-wrap">
          {['ORIGINAL', 'COPIA', 'DUPLICADO'].map((l) => (
            <button
              key={l}
              type="button"
              disabled={disabled}
              onClick={() => set({ copyLabel: l })}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all disabled:cursor-not-allowed ${
                settings.copyLabel === l
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Mostrar secciones</p>
        <div className="divide-y divide-slate-100">
          {toggleRow('Logotipo', 'showLogo')}
          {toggleRow('Historial de pagos', 'showPaymentHistory')}
          {toggleRow('Línea de firma', 'showSignatureLine')}
          {toggleRow('Notas de la factura', 'showNotes')}
          {toggleRow('RNC / RUC', 'showRUC')}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Texto de pie de página</p>
        <textarea
          value={settings.footerText}
          disabled={disabled}
          onChange={(e) => set({ footerText: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none disabled:bg-slate-50 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
};

export default InvoicePrintSettingsForm;
